/**
 * auth API — FO 인증·회원가입 도메인 함수(003 member-signup 실 연동).
 *
 * 모든 호출은 단일 axios 인스턴스(`http`, withCredentials)를 경유한다 — 직접 axios/fetch 금지(헌법 I).
 * 글로벌 에러 처리는 http.ts 인터셉터 1회로 일원화 — 호출부 catch 에서 중복 alert 금지.
 *
 * 회원가입 엔드포인트(003, 056 이메일 인증 반영):
 *   POST /api/fo/auth/signup                              — 아이디/비밀번호 가입(201, **Set-Cookie 없음**).
 *   GET  /api/fo/auth/signup/google/context/{token}       — 구글 핸드오프 비소진 조회(peek).
 *   POST /api/fo/auth/signup/google                       — 구글 가입(consume + 생성, 201 + Set-Cookie).
 *
 * 이메일 인증(056):
 *   POST /api/fo/auth/verify-email                        — 인증 확인(성공 시 200 + Set-Cookie).
 *   POST /api/fo/auth/verify-email/resend                 — 인증메일 재발송(항상 200 — 계정 존재 비노출).
 *
 * 구글 SSO 진입·세션(002):
 *   GET    /api/fo/auth/oauth2/authorize/google — 브라우저 전체 리다이렉트(XHR 아님).
 *   GET    /api/fo/auth/oauth2/me               — 세션 확인.
 *   DELETE /api/fo/auth/oauth2/logout           — 세션 종료.
 *
 * 세션은 응답 본문이 아니라 HttpOnly 세션 쿠키(IPSI_SESSION)로 전달된다.
 * 🔴 **로컬 가입은 세션을 받지 않는다**(056 FR-002) — 이메일 인증 링크를 통과해야 로그인된다.
 * 구글 가입은 이미 이메일이 검증돼 있어 기존대로 가입 즉시 로그인이다(FR-003).
 */

import { http, type SilentRequestConfig } from '#/api/http';

/** 가입 동의 묶음 — 로컬·구글 공통(백엔드 SignupAgreements). */
export interface SignupAgreements {
  /** (필수) 만 14세 이상. */
  age14: boolean;
  /** (필수) 이용약관 동의. */
  terms: boolean;
  /** (필수) 개인정보 수집·이용 동의. */
  privacy: boolean;
  /** (선택③) 마케팅 활용 동의 — 회사가 연락하는 것. */
  marketing: boolean;
  /**
   * (선택②) 맞춤 정보 수집·이용 동의 — 화면 안에서 보여주는 것.
   * 마케팅(③)과 독립이며, 미동의여도 가입·구매·다운로드에 제한이 없다(개보법 §22⑤).
   */
  profileSegment: boolean;
}

/**
 * 가입 선택② 세그먼트(백엔드 SignupSegment) — 설계 10번 §4.3.
 *
 * ⚠️ **전 필드 선택 입력이다.** 미입력·미전송이어도 가입이 완료돼야 하며, 화면에서 필수 검증에
 * 넣으면 개보법 §22⑤ 위반이 된다. 미입력은 `null`/`''` 로 그대로 보낸다.
 */
export interface SignupSegment {
  /** 이용자 유형(STUDENT·PARENT·TEACHER·COUNSELOR·INSTRUCTOR·ETC). CONSULTANT 는 선택지 미노출(V951). */
  memberRole: string | null;
  /** 기타 자유입력 — 역할이 ETC 일 때만 서버가 반영한다. */
  memberRoleEtc: string | null;
  /** 학년(H1·H2·H3·N) — 학생=본인, 학부모=자녀. */
  studentGrade: string | null;
  /** 거주 시도 코드(SEOUL·GYEONGGI… 17종). */
  region: string | null;
  /** 고교 마스터 식별자(자동완성 선택) — 정상 경로. */
  schoolId: number | null;
  /** 마스터 미매칭 학교명 — schoolId 가 있으면 서버가 버린다(배타). */
  schoolEtc: string | null;
}

/** 아이디/비밀번호 회원가입 요청(백엔드 LocalSignupRequest). */
export interface LocalSignupPayload {
  /** 로그인 아이디(^[a-z][a-z0-9_]{3,19}$). */
  username: string;
  password: string;
  passwordConfirm: string;
  email: string;
  /** 이름(필수, trim 후 2~20자) — `sys_user.nick_name` 에 저장된다(053). */
  name: string;
  agreements: SignupAgreements;
  /** 선택② 세그먼트 — 미입력이면 전부 null 로 보낸다(가입은 그대로 완료). */
  segment: SignupSegment;
}

/** 구글 회원가입 요청(백엔드 GoogleSignupRequest) — 비밀번호·이메일 미수령. */
export interface GoogleSignupPayload {
  handoffToken: string;
  /**
   * 이름(필수, trim 후 2~20자) — 이메일·providerId 와 달리 **클라이언트가 운반한다**.
   * 프리필된 구글 계정 이름을 사용자가 고칠 수 있어야 하기 때문(학부모가 자녀 이름을 넣는 정상 흐름, 053 FR-003).
   */
  name: string;
  agreements: SignupAgreements;
  /** 로컬과 동일 계약 — 여기를 빠뜨리면 SSO 가입자만 세그먼트가 통째로 비게 된다. */
  segment: SignupSegment;
}

/** 회원가입 성공 응답(백엔드 SignupResponse). */
export interface SignupResult {
  /** 발급된 로그인 아이디(로컬=입력, 구글=자동 생성). */
  username: string;
  /**
   * 가입 대상 이메일 — 인증 안내 화면이 "어디로 보냈는지" 표시하는 데 쓴다(056 FR-002a).
   * 사용자가 방금 제출한 값이라 새로 노출되는 정보가 없다.
   */
  email: string;
  /**
   * 인증 링크 유효 기간(시간) — 안내 문구에 쓴다.
   *
   * ⚠️ **화면에 숫자를 박지 않는다.** 서버 설정(`ipsi.verification.token-ttl-seconds`)에서 파생된 값이라,
   * 운영에서 TTL 을 조정하면 이 값이 따라 바뀐다. 프론트에 24 를 하드코딩하면 조정 시
   * 실제 만료 시각과 안내가 어긋난다.
   */
  validHours: number;
}

/**
 * 로컬 로그인 요청 페이로드 — 아이디/비밀번호.
 * 식별자는 회원가입(username 으로 가입)과 정합하도록 username 을 쓴다(이메일 아님).
 */
export interface LoginPayload {
  username: string;
  password: string;
}

/** 로그인 성공 응답(백엔드 FoLoginResponse) — 로그인한 아이디. */
export interface LoginResult {
  username: string;
}

/**
 * 아이디/비밀번호 로그인 — `POST /api/fo/auth/login`.
 * 성공 시 백엔드가 HttpOnly 세션 쿠키(IPSI_SESSION)를 내려 즉시 로그인 상태가 된다(200). 본문은 username 만 반환한다.
 * 실패는 사유 구분 없이 401(일반화 — 계정 열거 방지)이며, 글로벌 에러는 http.ts 인터셉터가 토스트로 처리한다(호출부는 흐름 제어만).
 */
export async function login(payload: LoginPayload): Promise<LoginResult> {
  const { data } = await http.post<LoginResult>('/fo/auth/login', payload);
  return data;
}

/** 인증메일 재발송 결과(백엔드 ResendVerificationResponse). */
export interface ResendVerificationResult {
  /**
   * 다음 재발송까지 남은 대기 시간(초). 쿨다운 중이면 남은 시간, 그 외에는 전체 쿨다운 값이다.
   *
   * ⚠️ 이 값으로 "발송됐는지"를 판단할 수 없다 — 백엔드가 대상의 존재·인증 여부와 무관하게
   * 항상 200 을 돌려주기 때문이다(056 FR-017, 계정 존재 비노출). 화면도 "보냈다면 도착합니다"
   * 톤으로 안내하고 성공/실패를 단정하지 않는다.
   */
  cooldownSeconds: number;
  /**
   * 인증 링크 유효 기간(시간) — 서버 설정에서 파생된다(SignupResult.validHours 와 동일 출처).
   * 재발송 경로로 진입한 화면도 같은 안내 문구를 쓰므로 함께 내려받는다.
   */
  validHours: number;
}

/**
 * 인증메일 재발송 — `POST /api/fo/auth/verify-email/resend`.
 *
 * 대상은 **직전 단계에서 이미 특정된 계정**이다(056 FR-015a) — 사용자가 주소를 다시 입력하지 않는다.
 * 가입 직후 안내 화면은 가입 응답의 username 을, 로그인 차단 안내는 방금 입력한 아이디를 넘긴다.
 * 실패는 400(아이디 누락)뿐이며 그 외 모든 상태가 200 으로 수렴한다.
 */
export async function resendVerificationEmail(username: string): Promise<ResendVerificationResult> {
  const { data } = await http.post<ResendVerificationResult>('/fo/auth/verify-email/resend', {
    username,
  });
  return data;
}

/** 이메일 인증 확인 결과(백엔드 VerifyEmailResponse). */
export interface VerifyEmailResult {
  username: string;
  /**
   * 토큰은 유효했으나 대상 계정이 **이미 인증 완료**였음(056 FR-011). 세션이 발급되지 않으므로
   * 화면은 로그인으로 유도한다.
   *
   * ⚠️ 한 번 사용한 링크의 재클릭은 여기 해당하지 않는다 — 그 토큰은 이미 소진되어 404 다.
   */
  alreadyVerified: boolean;
}

/**
 * 이메일 인증 확인 — `POST /api/fo/auth/verify-email`.
 *
 * 토큰은 프론트 URL(`/verify-email?token=…`)에서 읽되 **본문으로** 보낸다 — 쿼리스트링은
 * 액세스 로그·Referer 에 남는다. 성공 시 백엔드가 세션 쿠키를 내려 즉시 로그인 상태가 된다.
 * 만료·이미 사용·위조는 구별 없이 404(056 FR-010) → 호출부가 실패 화면으로 분기한다.
 */
export async function verifyEmail(token: string): Promise<VerifyEmailResult> {
  const { data } = await http.post<VerifyEmailResult>('/fo/auth/verify-email', { token });
  return data;
}

/**
 * 아이디/비밀번호 회원가입 — `POST /api/fo/auth/signup`.
 *
 * 🔴 **세션 쿠키가 내려오지 않는다**(056 FR-002) — 인증 메일이 발송되고, 링크를 통과해야 로그인된다.
 * 호출부는 응답의 email 을 인증 안내 화면으로 넘긴다(홈으로 보내면 로그아웃 상태로 보인다).
 * 글로벌 에러(400 검증·409 중복)는 http.ts 인터셉터가 토스트로 처리 — 호출부는 흐름 제어만.
 */
export async function signup(payload: LocalSignupPayload): Promise<SignupResult> {
  const { data } = await http.post<SignupResult>('/fo/auth/signup', payload);
  return data;
}

/**
 * 구글 OAuth 회원가입 — `POST /api/fo/auth/signup/google`.
 * 핸드오프 토큰을 서버가 consume 해 검증 컨텍스트(이메일·providerId)를 직접 취득한다(클라 미운반).
 * 성공 시 세션 쿠키로 즉시 로그인. 무효 토큰은 404(인터셉터 토스트).
 */
export async function signupGoogle(payload: GoogleSignupPayload): Promise<SignupResult> {
  const { data } = await http.post<SignupResult>('/fo/auth/signup/google', payload);
  return data;
}

// ── 구글 SSO ──────────────────────────────────────────────────────────────

/** 세션 확인 응답 — 유효 세션이면 인증 정보, 미인증이면 null. */
export interface SessionInfo {
  /** 인증 여부(유효 세션이면 true). */
  authenticated: boolean;
  /** 로그인 사용자 이메일. */
  email: string;
}

/**
 * FO 세션 확인 — 영속 쿠키(IPSI_SESSION)만으로 로그인 상태를 복원한다(FR-017·SC-011).
 *
 * 앱 부팅 시 1회 호출해, 쿠키 세션이 살아있으면 SessionInfo(200), 미로그인·만료면 null(401)을 반환한다.
 * 미로그인 401 은 **기대된 정상 응답**이므로 `silent` 로 전역 토스트·clearSession 을 우회한다
 * (http.ts 인터셉터가 silent 요청의 에러를 조용히 전파). 401 외 에러(네트워크 등)는 호출부가 null 로 흡수해
 * 부팅이 막히지 않게 한다.
 */
export async function fetchSession(): Promise<SessionInfo | null> {
  try {
    const { data } = await http.get<SessionInfo>('/fo/auth/oauth2/me', {
      silent: true,
    } as SilentRequestConfig);
    return data;
  } catch {
    // 401(미로그인)·네트워크 오류 등 모든 실패를 "복원할 세션 없음"으로 흡수 — 부팅을 막지 않는다.
    // (보호 라우트 접근은 가드가 guest 로 별도 차단하므로 안전.)
    return null;
  }
}

/**
 * 구글 가입 핸드오프 표시 컨텍스트 — 회원가입 페이지 로드 시 검증 이메일을 표시(readonly)하기 위한
 * <b>비소진 peek</b> 결과(백엔드 HandoffContextResponse). 노출 최소화로 providerId 는 포함하지 않으며,
 * providerId 는 가입 제출 시 서버가 consume 으로 직접 취득한다(신뢰 입력).
 */
export interface HandoffContext {
  /** 구글에서 확인된 이메일 주소(표시·readonly). */
  email: string;
  /** 요청 채널(예: 'FO'). */
  channel: string;
  /**
   * 구글 계정 이름 — **프리필 전용이며 없을 수 있다**(구글 미제공 시 null).
   * 이름 칸은 readonly 가 아니다 — 사용자가 고친 값이 저장된다(053 FR-003·FR-006).
   */
  name?: null | string;
}

/**
 * 구글 SSO 인증 URL 반환 — `window.location.href` 로 이동하면 구글 동의 화면으로 리다이렉트됨.
 *
 * 경로는 `/api/fo/auth/oauth2/authorize/google` 고정. `VITE_API_BASE_URL` 이 설정된 경우
 * 해당 오리진을 prefix 로 사용하고, 없으면 현재 페이지 오리진과 동일 오리진 경로로 반환한다.
 *
 * @example
 *   window.location.href = googleLoginUrl();
 */
export function googleLoginUrl(): string {
  const path = '/api/fo/auth/oauth2/authorize/google';
  // VITE_API_BASE_URL 이 설정된 경우: 해당 값이 'https://api.example.com/api' 처럼 /api 를
  // 이미 포함할 수 있으므로, path 가 /api 로 시작하고 base 도 /api 로 끝나면 중복을 제거한다.
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  if (!base || base === '/api') {
    // 동일 오리진 — 상대 경로(/api/fo/...) 를 브라우저가 현재 오리진으로 해석.
    return path;
  }
  // 절대 URL(예: https://api.myipsi.co.kr/api) — /api 중복 제거 후 결합.
  const origin = base.replace(/\/api\/?$/, '');
  return `${origin}${path}`;
}

/**
 * 구글 가입 핸드오프 컨텍스트 조회(peek) — `GET /api/fo/auth/signup/google/context/{token}`.
 *
 * 비소진(GET만)이라 TTL 내 재조회가 가능하다 — 페이지 로드 시 검증 이메일 표시용. 보안 임계 단발성은
 * 가입 제출 시 백엔드 consume 이 보증한다(D4). 무효·만료·위조 토큰은 404 → http.ts 인터셉터가 토스트 처리하므로
 * 호출부는 흐름 제어(만료 안내 분기)만 담당한다.
 *
 * @param handoffToken 백엔드가 `/signup?handoffToken=...` 로 전달한 단기 토큰.
 */
export async function peekHandoff(handoffToken: string): Promise<HandoffContext> {
  const { data } = await http.get<HandoffContext>(
    `/fo/auth/signup/google/context/${encodeURIComponent(handoffToken)}`,
  );
  return data;
}

/**
 * FO 로그아웃 — 서버 세션(HttpOnly IPSI_SESSION 쿠키)을 백엔드에서 종료한다.
 * `DELETE /api/fo/auth/oauth2/logout` 가 Redis 세션을 무효화하고 쿠키를 만료(Set-Cookie Max-Age=0)한다.
 * 프론트 로컬 상태 초기화만으로는 서버 세션·쿠키가 잔존하므로 반드시 이 호출 후 스토어를 정리한다.
 * 글로벌 에러는 http.ts 인터셉터가 처리 — 호출부는 흐름 제어만 담당.
 */
export async function logout(): Promise<void> {
  await http.delete('/fo/auth/oauth2/logout');
}
